/**
 * @fileoverview Pure surface-gating logic for the global Locus mount
 * (FRE-1229, PRD §6.3).
 *
 * Resolves the current location — client pathname + the host classifier's
 * surface label — into the PRD's route-group matrix and, when the route group
 * is Locus-eligible, the `LocusSurface` value every `/api/locus` request will
 * carry:
 *
 * | Route group                      | Signed-in            | Signed-out          |
 * | -------------------------------- | -------------------- | ------------------- |
 * | `(city)` — `clt.loci.city`       | Full launcher → panel | Sign-up CTA (FRE-1230) |
 * | `(app)` — `app.loci.city`        | Full launcher → panel | N/A — auth-gated    |
 * | `(apex)` — `loci.city`           | Full (rare)          | Sign-up CTA (FRE-1230) |
 * | `(admin)` — CMS                  | Not mounted          | Not mounted         |
 * | `(dynamic)` — venue/artist pages | Full launcher → panel | Sign-up CTA (FRE-1230) |
 *
 * Session state is deliberately NOT an input here — this module answers only
 * "which route group is this, and which Locus surface does it map to?".
 * `LocusGlobalMount` composes the answer with the live session + the
 * `locus-runtime` feature flag.
 *
 * Route groups are invisible in URLs, so the resolution is a hybrid:
 * path-shape overrides for the groups that are path-addressable everywhere
 * (`(admin)` → `/admin/*`, `(dynamic)` → `/artist/:slug` / `/venue/:slug`),
 * then the host classifier's surface label (`lib/city-context.ts`, mirrored
 * client-side by `CitySurfaceProvider`) for the host-level groups.
 */

import type { CitySurfaceLabel } from '@/lib/city-context';

import type { LocusSurface } from './useLocus';

/**
 * The five PRD §6.3 route groups, plus `'none'` for locations outside the
 * matrix (fallback hosts, auth pages, `/coming-soon`).
 */
export type LocusRouteGroup = 'city' | 'app' | 'apex' | 'admin' | 'dynamic' | 'none';

/** The resolved mount decision. `surface: null` ⇒ Locus does not mount. */
export interface LocusMountDecision {
  readonly routeGroup: LocusRouteGroup;
  /** `LocusSurface` to send on `/api/locus` requests, or `null` = no mount. */
  readonly surface: LocusSurface | null;
}

/**
 * City-less standalone paths admitted on the app host that belong to no
 * PRD route group: the `(auth)` group's flows plus the unknown-city rewrite
 * target. A conversational overlay has no business on a login form, and a
 * signed-in visitor passing through these is mid-redirect anyway.
 */
const EXCLUDED_PATH_PREFIXES: readonly string[] = [
  '/login',
  '/signup',
  '/logout',
  '/forgot-password',
  '/reset-password',
  '/coming-soon',
];

/** Collapse trailing slashes; guarantee a leading one. Never returns `''`. */
function normalizePathname(pathname: string): string {
  const withLead = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const trimmed = withLead.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

/** `true` when `path` is `prefix` itself or a deeper segment under it. */
function hasSegmentPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

const NOT_MOUNTED: LocusMountDecision = { routeGroup: 'none', surface: null };

/**
 * Resolve the PRD §6.3 mount decision for the current location.
 *
 * @param pathname - Client pathname (`usePathname()` shape — no query/hash).
 * @param citySurface - The host classifier's surface label
 *   (`useCitySurface()`), resolved server-side by `proxy.ts`.
 */
export function resolveLocusMount(
  pathname: string,
  citySurface: CitySurfaceLabel,
): LocusMountDecision {
  const path = normalizePathname(pathname);

  // `(admin)` — excluded unconditionally regardless of session or host:
  // internal tool, different data-sensitivity posture (PRD §6.3). Checked
  // first, on every surface, as defense in depth.
  if (hasSegmentPrefix(path, '/admin')) {
    return { routeGroup: 'admin', surface: null };
  }

  // Fallback hosts (`localhost`, `*.vercel.app`, `freqseek.io`) are outside
  // the PRD matrix — real surfaces are exercised locally via the
  // `*.loci.localhost` dev hosts (docs/SURFACE_ROUTING.md).
  if (citySurface === 'fallback') {
    return NOT_MOUNTED;
  }

  // `(dynamic)` — entity detail pages, path-addressable on the city host.
  // (`/artists` — the apex marketing page — does not match `/artist/`.)
  if (path.startsWith('/artist/')) {
    return { routeGroup: 'dynamic', surface: 'artist-detail' };
  }
  if (path.startsWith('/venue/')) {
    return { routeGroup: 'dynamic', surface: 'venue-detail' };
  }

  // City-less `(auth)` flows + `/coming-soon` belong to no PRD route group.
  if (EXCLUDED_PATH_PREFIXES.some((prefix) => hasSegmentPrefix(path, prefix))) {
    return NOT_MOUNTED;
  }

  switch (citySurface) {
    case 'marketing-apex':
      return { routeGroup: 'apex', surface: 'apex' };
    case 'marketing-city':
      // `(city)`'s event detail page gets the finer-grained surface the
      // `/api/locus` enum already distinguishes (FRE-1219).
      return path.startsWith('/event/')
        ? { routeGroup: 'city', surface: 'event-detail' }
        : { routeGroup: 'city', surface: 'city' };
    case 'app':
      return { routeGroup: 'app', surface: 'app' };
    default:
      return NOT_MOUNTED;
  }
}
